import { IconName } from '../components/Icons/IconNames';
import { logger } from '../utils/logger';
import type { OptionalProperties, RequiredProperties } from '../utils/types';
import type { ParameterLike, StrategyLike } from './OrderStrategy.types';
import { ParameterTypeEnum } from './OrderStrategy.types';
import type {
  AlgoTypeEnum,
  IParametersEnumValues,
  IStrategy,
  IStrategy4860Parameters,
  OrdTypeEnum,
  StrategyScopeEnum,
  UpdateActionEnum,
} from './types';
import { PresenceEnum } from './types';
import type { WLOrderStrategy } from './WLOrderStrategy';

/**
 * Note the types in this file should really only be used for Orders in Principal and not Orders in White Label
 */

type InstrumentScope = 'Native' | 'Synthetic' | 'All';

/**
 * JSON types are because the autogenerated ones do not properly represent the optionality of some of these properties
 */
export type JSON_StrategyParameter = RequiredProperties<
  OptionalProperties<
    // Overwrite autogenerated type to allow strings or enum for fields.
    Omit<IStrategy4860Parameters, 'Presence'> & {
      Presence: string | PresenceEnum;
    },
    'Metadata' | 'EnumValues' | 'DefaultValue' | 'ParameterID' | 'Description'
  >,
  'Name' | 'DisplayName' | 'Type' | 'Presence'
>;

export type JSON_OrderStrategy = RequiredProperties<
  OptionalProperties<
    // This overwrites the autogenerated type with a more accurate one for Parameters
    Omit<IStrategy, 'Parameters' | 'AlgoType' | 'Strategy'> & {
      Parameters: JSON_StrategyParameter[];
      AlgoType: string | AlgoTypeEnum;
    },
    'Metadata' | 'UpdateAction' | 'Revision' | 'AlgoType' | 'Description'
  >,
  'Name' | 'DisplayName' | 'Group' | 'StrategyScope'
>;

type OrderStrategyMetadata = {
  CanBeModified?: boolean;
  PriceIntervalEnd?: string;
  PreTradeEnabled?: boolean;
};

// TODO copy the pattern from TotalTradingVolume
export class OrderStrategy implements StrategyLike {
  public static StreamName = 'OrderStrategy';

  AlgoType: AlgoTypeEnum | undefined;
  Description: string;
  DisplayName: string;
  Group: string;
  InstrumentScope: InstrumentScope;
  Metadata: OrderStrategyMetadata;
  Name: string;
  Parameters: Parameter[];
  Revision?: number;
  // StrategyScope is only present on Principal OrderStrategies
  StrategyScope?: StrategyScopeEnum;
  UpdateAction?: UpdateActionEnum;
  // OrdType only exists on WL OrderStrategies. In principal this is encoded in the Parameters
  OrdType?: OrdTypeEnum;

  get supportsUnifiedLiquidity(): boolean {
    return this.Parameters.some(param => param.Name === ParameterTypeEnum.UnifiedLiquidity);
  }

  get supportsDuration(): boolean {
    return this.Parameters.some(param => param.Name === ParameterTypeEnum.Duration);
  }

  get isManual(): boolean {
    // TODO don't base this on Name. Make a fake type instead
    return this.Name === 'Manual';
  }

  get showPriceAsRange(): boolean {
    return this.endPriceParamName !== undefined;
  }

  get endPriceParamName(): string | undefined {
    const endParamName = this.Metadata.PriceIntervalEnd || 'ScaleEndPrice'; // TODO get rid of ScaleEndPrice default
    return this.Parameters.find(param => param.Name === endParamName)?.Name;
  }

  get canBeModified(): boolean {
    return this.Metadata.CanBeModified !== false;
  }

  get supportsPreTrade(): boolean {
    return this.Metadata.PreTradeEnabled === true;
  }

  /**
   * Returns the primary parameter for the given section name, or undefined if none exists.
   */
  getPrimaryParameterForSection(sectionName: string): Parameter | undefined {
    return this.Parameters.find(p => p.isPrimary && p.section === sectionName);
  }

  /**
   * Returns all parameters belonging to the given section name.
   */
  getSectionParameters(sectionName: string): Parameter[] {
    return this.Parameters.filter(p => p.section === sectionName);
  }

  /**
   * True when the strategy has the parameter-driven DDH section (DynamicDeltaHedgingEnabled),
   * meaning we use the new approach. False means we fall back to the legacy hardcoded DynamicDeltaHedge UI.
   *
   * TODO: Remove this getter once the backend has been migrated to include DynamicDeltaHedgingEnabled
   * in the StrategyParameters for all relevant strategies.
   */
  get hasParameterDrivenDDH(): boolean {
    return this.Parameters.some(p => p.Name === 'DynamicDeltaHedgingEnabled');
  }

  constructor(data: JSON_OrderStrategy | OrderStrategy | WLOrderStrategy) {
    this.AlgoType = (data as OrderStrategy).AlgoType || undefined;
    this.Description = data.Description || '';
    this.DisplayName = data.DisplayName;
    this.Group = data.Group;
    this.Name = data.Name;
    this.Revision = (data as OrderStrategy).Revision || 0;
    this.StrategyScope = (data as OrderStrategy).StrategyScope || undefined;
    this.UpdateAction = (data as OrderStrategy).UpdateAction || undefined;
    this.InstrumentScope = (data as OrderStrategy).InstrumentScope || undefined;
    this.OrdType = (data as OrderStrategy).OrdType || undefined;
    this.Metadata = parseMetadata(data);
    this.Parameters = parseParameters(data.Parameters as JSON_StrategyParameter[]);
  }
}

function parseMetadata(
  data: JSON_OrderStrategy | JSON_StrategyParameter | OrderStrategy | WLOrderStrategy
): Record<string, unknown> {
  if (!('Metadata' in data)) {
    return {};
  }
  if (typeof data.Metadata === 'object') {
    return data.Metadata;
  }
  if (typeof data.Metadata !== 'string') {
    return {};
  }
  try {
    return JSON.parse(data.Metadata);
  } catch (e) {
    logger.warn(`Failed to parse ${data.Name} metadata: ${data.Metadata} - ${e}`);
    return {};
  }
}

function parseParameters(parameters: JSON_StrategyParameter[] = []): Parameter[] {
  return ensureStartTimeEndTimeOrdering(parameters).map(param => new Parameter(param));
}

/**
 * Ensures that EndTime is placed directly after StartTime
 * Returns a copy of the given array. Does not mutate original.
 * If both StartTime and EndTime do not exist, or they already placed correctly relative to each other,
 * we return the passed in array
 * @param parameters the parameter list to check
 * @returns parameter list with fixed ordering of StartTime and EndTime
 */
export const ensureStartTimeEndTimeOrdering = (parameters: JSON_StrategyParameter[]): JSON_StrategyParameter[] => {
  const startTimeIndex = parameters.findIndex(param => param.Name === 'StartTime');
  const endTimeIndex = parameters.findIndex(param => param.Name === 'EndTime');

  if (startTimeIndex === -1 || endTimeIndex === -1) {
    return parameters;
  }

  if (endTimeIndex !== startTimeIndex + 1) {
    // EndTime param is not directly after StartTime. Put EndTime directly after StartTime
    const startTimeParam = parameters[startTimeIndex];
    const endTimeParam = parameters[endTimeIndex];
    const newParameters = parameters.flatMap(param => {
      if (param.Name === 'StartTime') {
        // Whenever we find StartTime, put both StartTime and EndTime here (note that we use flatMap)
        return [startTimeParam, endTimeParam];
      } else if (param.Name === 'EndTime') {
        // Whenever we find EndTime, filter out since it's being added upon finding StartTime
        return [];
      } else {
        return [param];
      }
    });

    return newParameters;
  }

  // do nothing
  return parameters;
};

// Predefined unit types that can be specified directly in UnitsParam
export const ParameterUnitType = {
  /** Price Units - same units as the Security's quote currency */
  PriceUnits: 'PriceUnits',
  /** Price Increments - number of price increments based on Security */
  PriceIncrements: 'PriceIncrements',
  /** Basis Points - basis points or "bps" */
  BasisPoints: 'BasisPoints',
} as const;

export type ParameterUnitType = (typeof ParameterUnitType)[keyof typeof ParameterUnitType];

interface ParameterMetadata {
  // 'none' means no units will be displayed. undefined will fall back to default behavior which is the order currency
  Units?: 'none';
  // If present, the parameter will only be shown if the given parameter is set
  ShowIfParamSet?: string;
  // The section name that this parameter belongs to
  Section?: string;
  // The name of the parameter that is the units for this parameter
  UnitsParam?: string;
  // Options array for Units type parameters - defines available unit types
  Options?: Array<{ name: string; description: string }>;
  // If true, this parameter is the primary parameter for its section (controls section title and visibility)
  Primary?: boolean;
  // Icon name for the section (when this is the primary parameter)
  SectionIcon?: string;
  // For Symbol type parameters: the filter to apply to the security selector (e.g. 'HedgeSymbol')
  filter?: string;
  // For primary section parameters: gate the entire section to a specific instrument type (e.g. 'Options')
  // For Aggregations: filter the aggregation control to the given symbol property (e.g. 'Symbol' for the order symbol,
  // or 'DynamicDeltaHedgeSymbol' for the hedge symbol)
  SymbolFilter?: string;
  // For primary section parameters: the payload key under which non-HedgeStrategy child parameters are nested
  payloadKey?: string;
  // For UnifiedLiquidity parameters inside a payloadKey section: the Name of a sibling Symbol parameter
  // whose selected security should be used for the unified-liquidity token lookup instead of the order's
  // main symbol. E.g. 'DynamicDeltaHedgeSymbol' on the DDH UnifiedLiquidity parameter.
  symbolParamRef?: string;
}

const ICON_NAME_VALUES: Set<string> = new Set(Object.values(IconName));

const NUMBER_PARAMETER_TYPES = [
  ParameterTypeEnum.Integer,
  ParameterTypeEnum.Bps,
  ParameterTypeEnum.Percent,
  ParameterTypeEnum.Price,
  ParameterTypeEnum.PriceOffset,
  ParameterTypeEnum.Qty,
  ParameterTypeEnum.Units,
  ParameterTypeEnum.Duration,
  ParameterTypeEnum.Interval,
  ParameterTypeEnum.TargetParticipationRate,
];

/**
 * The following props are omitted from interface for given reason
 * DefaultValue - Can sometimes be undefined
 * Metadata - Comes over wire as string, but is parsed into object
 * EnumValues - Can sometimes be undefined
 * ParameterID - Not used in the frontend
 */
export class Parameter implements ParameterLike {
  DefaultValue: IStrategy4860Parameters['DefaultValue'] | undefined;
  Type: ParameterTypeEnum;
  Name: string;
  DisplayName: string;
  Description: string;
  ParameterID?: number;
  Presence: PresenceEnum;
  EnumValues: IParametersEnumValues[] | undefined;
  Metadata: ParameterMetadata;

  get units() {
    return this.Metadata?.Units;
  }

  get showIfParamSet() {
    return this.Metadata?.ShowIfParamSet;
  }

  get section() {
    return this.Metadata?.Section;
  }

  get unitsParam() {
    return this.Metadata?.UnitsParam;
  }

  get isPrimary() {
    return this.Metadata?.Primary === true;
  }

  get sectionIcon(): IconName | undefined {
    const icon = this.Metadata?.SectionIcon;
    if (!icon) {
      return undefined;
    }
    return ICON_NAME_VALUES.has(icon) ? (icon as IconName) : undefined;
  }

  get options(): { name: string; description: string }[] {
    return this.Metadata?.Options || [];
  }

  get filter() {
    return this.Metadata?.filter;
  }

  get symbolFilter() {
    return this.Metadata?.SymbolFilter;
  }

  get payloadKey() {
    return this.Metadata?.payloadKey;
  }

  get symbolParamRef() {
    return this.Metadata?.symbolParamRef;
  }

  /**
   * Checks if this parameter type is a number type
   * @returns true if the parameter type represents a numeric value
   */
  get isNumberType(): boolean {
    return NUMBER_PARAMETER_TYPES.includes(this.Type);
  }

  /**
   * Checks if this parameter should be visible. Returns false in cases like Units parameters which are shown in line
   * with the referenced parameter.
   * @param strategy The current strategy containing all parameters
   * @param parameterFields The current parameter field values
   * @returns true if the parameter should be visible, false otherwise
   */
  shouldBeVisible(strategy: OrderStrategy | undefined, parameterFields: { [key: string]: { value: any } }): boolean {
    // Parameters that are referenced as units for another parameter should not be visible
    // They modify how the referenced parameter is displayed
    if (strategy) {
      if (this.Type === ParameterTypeEnum.Units) {
        return false;
      }
    }

    // Check basic visibility rules first
    const baseVisibility =
      this.Presence !== PresenceEnum.Hidden &&
      // End price parameters are shown elsewhere and should be excluded from favorites
      !(strategy?.showPriceAsRange && strategy.endPriceParamName === this.Name);

    if (!baseVisibility) {
      return false;
    }

    if (!this.showIfParamSet) {
      return true; // No conditional visibility, show the parameter
    }

    // Find the referenced parameter in the strategy
    const referencedParam = strategy?.Parameters.find(p => p.Name === this.showIfParamSet);
    if (!referencedParam) {
      logger.warn(
        `Parameter "${this.Name}" references non-existent parameter "${this.showIfParamSet}" in ShowIfParamSet. Showing parameter as fallback.`
      );
      return true; // Show as fallback when referenced parameter doesn't exist
    }

    // Check if the referenced parameter has a value set
    const referencedField = parameterFields[this.showIfParamSet];
    if (!referencedField) {
      return true; // Referenced field doesn't exist in current fields, show the parameter as fallback.
    }

    // Consider parameter "set" if it has any non-empty string value or is "Disabled", (this might need to be expanded)
    const value = referencedField.value;
    if (value === 'Disabled') {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    // For non-string values, consider truthy values as "set"
    return Boolean(value);
  }

  /**
   * Checks whether this parameter belongs to a section whose controlling parameter
   * is disabled. Two mechanisms are checked:
   * 1. ShowIfParamSet – explicit dependency on another parameter's value
   * 2. Section + Primary – implicit dependency via section grouping
   *
   * Note: the weird API of taking the get function avoids a circular dependency and deals with this weird legacy codepath
   * where we are still camelCasing parameter names in the order parameters object.
   *
   * @param strategyParameters The full list of strategy parameters (for finding the primary parameter)
   * @param getParameterValue A function that returns the current value for a parameter given its Name
   */
  isInDisabledSection(strategy: OrderStrategy | undefined, getParameterValue: (paramName: string) => unknown): boolean {
    if (this.showIfParamSet) {
      if (isSectionControllingValueDisabled(getParameterValue(this.showIfParamSet))) {
        return true;
      }
    }

    if (this.section && !this.isPrimary) {
      const primaryParam = strategy?.getPrimaryParameterForSection(this.section);
      if (primaryParam) {
        if (isSectionControllingValueDisabled(getParameterValue(primaryParam.Name))) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Gets the unit options for this parameter if it has one
   */
  getUnitOptions(strategy: OrderStrategy | undefined): { name: string; description: string }[] | undefined {
    if (!this.unitsParam || !strategy) {
      return undefined;
    }
    return strategy.Parameters.find(param => param.Name === this.unitsParam && param.Type === ParameterTypeEnum.Units)
      ?.options as { name: string; description: string }[];
  }

  constructor(data: JSON_StrategyParameter) {
    this.DefaultValue = data.DefaultValue;
    this.Name = data.Name;
    this.DisplayName = data.DisplayName;
    this.Description = data.Description || '';
    this.ParameterID = data.ParameterID;
    this.Presence = data.Presence as PresenceEnum;
    this.EnumValues = data.EnumValues;
    // TODO maybe parse this and error if it's not a valid type
    this.Type = data.Type as ParameterTypeEnum;
    this.Metadata = parseMetadata(data);
  }
}

/**
 * The contract here for what we are calling a "disabled" section is a bit rough for now. Might need to change as we
 * add more sections...
 */
function isSectionControllingValueDisabled(value: unknown): boolean {
  return value === undefined || value === 'Disabled' || (typeof value === 'string' && value.trim() === '');
}
